import AutoLayout from "@src/deprecatedDesignSystem/components/AutoLayout";
import { FC, useState } from "react";
import { StepHeader } from "./StepHeader";
import { gql } from "@apollo/client";
import { StyleSheet } from "aphrodite";
import { useHrisActivationDataQuery } from "./ActivateStep.generated";
import Text from "@ui/text";
import { deprecatedTones } from "@src/deprecatedDesignSystem/styles/deprecatedColors";
import DeprecatedIcon from "@src/deprecatedDesignSystem/components/DeprecatedIcon";
import { DeprecatedIconType } from "@src/deprecatedDesignSystem/deprecatedIcons";
import Spinner from "@src/deprecatedDesignSystem/components/Spinner";

type Props = {
  hrisCsvUploadId?: string;
};

export const ActivateStep: FC<Props> = ({ hrisCsvUploadId }) => {
  const { data, loading } = useHrisActivationDataQuery(
    hrisCsvUploadId ? { variables: { hrisCsvUploadId } } : undefined,
  );

  if (loading) {
    return <Spinner centered />;
  }
  return (
    <AutoLayout
      direction="vertical"
      style={{ flex: 1, alignSelf: "stretch" }}
      paddingBottom={20}
    >
      <StepHeader
        step="activate"
        inHrisCsvSetup={hrisCsvUploadId !== undefined}
      />
      <AutoLayout
        marginTop={32}
        width={524}
        direction="vertical"
        styleDeclaration={styles.container}
        spaceBetweenItems={12}
      >
        {data && (
          <>
            {data.HrisActivationData.linkedEmployees.length > 0 && (
              <Section
                names={data.HrisActivationData.linkedEmployees.map(
                  (e) => e.firstName + " " + e.lastName,
                )}
                resource="employees"
                verb="Link"
                icon="user-minus"
              />
            )}

            {data.HrisActivationData.deactivatedEmployees.length > 0 && (
              <Section
                names={data.HrisActivationData.deactivatedEmployees.map(
                  (e) => e.firstName + " " + e.lastName,
                )}
                resource="employees"
                verb="Deactivate"
                icon="user-minus"
              />
            )}
            {data.HrisActivationData.createdLocations.length > 0 && (
              <Section
                names={data.HrisActivationData.createdLocations.map(
                  (loc) => loc.name,
                )}
                resource="locations"
                verb="Create"
                icon="user-minus"
              />
            )}
            {data.HrisActivationData.linkedLocations.length > 0 && (
              <Section
                names={data.HrisActivationData.linkedLocations.map(
                  (loc) => loc.name,
                )}
                resource="locations"
                verb="Link"
                icon="user-minus"
              />
            )}

            {data.HrisActivationData.createdTeams.length > 0 && (
              <Section
                names={data.HrisActivationData.createdTeams.map(
                  (team) => team.name,
                )}
                resource="roles"
                verb="Create"
                icon="user-minus"
              />
            )}
            {data.HrisActivationData.linkedTeams.length > 0 && (
              <Section
                names={data.HrisActivationData.linkedTeams.map(
                  (team) => team.name,
                )}
                resource="roles"
                verb="Link"
                icon="user-minus"
              />
            )}
          </>
        )}
      </AutoLayout>
    </AutoLayout>
  );
};

type SectionProps = {
  icon: DeprecatedIconType;
  verb: string;
  resource: string;
  names: string[];
};

const Section: FC<SectionProps> = ({ verb, resource, names, icon }) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <AutoLayout direction="vertical">
      <AutoLayout
        style={{ cursor: "pointer", userSelect: "none" }}
        spaceBetweenItems={8}
        onClick={() => setExpanded(!expanded)}
      >
        <DeprecatedIcon
          type={icon}
          color="#8B90A4"
          style={{
            backgroundColor: deprecatedTones.gray1,
            borderRadius: 8,
          }}
        />
        <Text type="P1" fontWeight="Medium">
          {verb}{" "}
          <Text type="P1" fontWeight="SemiBold" color={deprecatedTones.blue9}>
            {names.length}
          </Text>{" "}
          {resource}
        </Text>
        <DeprecatedIcon
          iconSize={24}
          type={expanded ? "chevron-down" : "chevron-up"}
        />
      </AutoLayout>
      {expanded && (
        <AutoLayout direction="vertical" paddingLeft={36} paddingTop={4}>
          {names.map((name) => (
            <Text type="P1" fontWeight="Medium" key={name} marginBottom={4}>
              {name}
            </Text>
          ))}
        </AutoLayout>
      )}
    </AutoLayout>
  );
};

gql`
  query HrisActivationData($hrisCsvUploadId: String) {
    HrisActivationData(hrisCsvUploadId: $hrisCsvUploadId) {
      linkedLocations {
        id
        name
      }
      createdLocations {
        id
        name
      }
      linkedTeams {
        id
        name
      }
      createdTeams {
        id
        name
      }
      linkedEmployees {
        id
        firstName
        lastName
      }
      deactivatedEmployees {
        id
        firstName
        lastName
      }
    }
  }
`;

const styles = StyleSheet.create({
  container: {
    boxShadow: "0px 0px 50px rgba(154, 146, 162, 0.21)",
    borderRadius: 20,
    backdropFilter: "blur(30px)",
    padding: "12px 16px 16px",
  },
  gear: {
    background:
      "linear-gradient(110.28deg, rgba(172, 165, 197, 0.2) 0%, rgba(172, 165, 197, 0) 100%)",
    backdropFilter: "blur(20px)",
    borderRadius: 12,
  },
});
